/**
 * Bubble numbers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_lettering`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const bubbleNumbersPack: ToilePack = {
    slug: 'bubble-numbers',
    category: 'illustration',
    name: 'Bubble numbers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'digits', label: '0 to 9', subject: 'a chart of the ten digits "0 1 2 3 4 5 6 7 8 9" as fat rounded bubble outlines in two even rows, every shape closed and empty'},
        {key: 'birthday', label: 'Birthday number', subject: 'a single giant bubble number "5" filling the page with a wide margin, a thick clean outline and a few small stars around it'},
        {key: 'counting', label: 'Counting chart', subject: 'a classroom chart of bubble numbers "1" to "20" in a four-by-five grid of boxes, each number centered in its box'},
    ],
    styles: [
        {key: 'plain', label: 'Plain outline', style: 'one even medium outline, perfectly round puffy curves, nothing inside the numbers', palette: 'black ink only', framing: 'centered on a white sheet with a generous margin'},
        {key: 'striped', label: 'Stripes inside', style: 'a bold puffy outline with simple parallel stripes drawn inside each number', palette: 'black ink only', framing: 'centered on a white sheet with a generous margin'},
        {key: 'dotted', label: 'Dots and stars', style: 'a bold puffy outline with a scatter of small dots and tiny stars drawn inside each number', palette: 'black ink only', framing: 'centered on a white sheet with a generous margin'},
    ],
    directions: ['number 7', 'hearts', 'a date'],
    directionHint: 'try: number 7',
    seo: {
        title: 'Bubble Numbers: Digits, Birthday, Counting Chart',
        metaDescription: 'Nine printable bubble number sheets: the digits 0 to 9, one big number and a counting chart to 20, in three outline styles.',
        h1: 'Bubble numbers: the digits 0 to 9, a birthday number, a counting chart',
        tagline: 'Nine sheets in three outline styles: a full set of digits, one big number and a chart to 20.',
        intro: 'Bubble numbers are puffy outline digits for birthday signs, classroom counting walls and scoreboards. The nine sheets are black on white: the ten digits in one chart, a single large number to cut out, and a counting chart from 1 to 20 in boxes. Each comes plain, with stripes inside, or with dots and tiny stars inside. Ask for another number, a date or hearts, and the same outline is drawn for it.',
        useCases: [
            'a birthday sign or age banner',
            'a classroom counting wall',
            'a scoreboard',
            "a number poster for a child's room",
        ],
        faq: [
            {q: 'Which numbers are in the set?', a: 'The first sheet has the digits 0 to 9, the second shows one large 5, and the third is a counting chart from 1 to 20.'},
            {q: 'Can I get a number other than 5, or a date?', a: 'Yes. Ask for another number, a date or hearts, and the same bubble outline is drawn for that subject. A different subject is a request, not a promised result.'},
            {q: 'What are the stripes and dots for?', a: 'They are patterns drawn inside the outline: parallel stripes on one set, and a scatter of dots and tiny stars on the other. The plain set has nothing inside.'},
            {q: 'Are the numbers meant to be colored in?', a: 'The plain outline is empty inside, ready to color. The striped and dotted sets have their patterns drawn inside each number.'},
        ],
    },
}
